import HomesideLogo from '../assets/homeside-logo.png';
import { colors, palette } from './theme.colors';

export enum FontSize {
  Fine = 'fine',
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  Inherit = 'inherit',
  H1 = 'H1',
  H2 = 'H2',
  H3 = 'H3',
  H4 = 'H4',
  H5 = 'H5',
  H6 = 'H6',
}

export enum FontType {
  Regular = 'regular',
  SemiBold = 'semibold',
  Strong = 'strong',
  Light = 'light',
}

const buttonType = {
  margin: 'initial',
  maxWidth: '400px',
  padding: '16px',
  radius: '99999px',
};

const inputType = {
  borderBottom: `3px solid ${palette.grey700}`,
  fontSize: FontSize.H6,
  fontWeight: FontType.Regular,
  letterSpacing: '8px',
  maxWidth: '390px',
  placeholderColor: palette.grey200,
  placeholderFontSize: '28px',
};

const logo = {
  boxShadow: '0 2px 10px rgba(0,0,0,.2)',
  image: HomesideLogo,
  margin: 'initial',
  paddingLeft: '20px',
  tabletWidth: '60%',
  width: '250px',
};

const uploadPage = {
  backgroundColor: palette.grey100,
  buttonCase: 'uppercase',
  containerMaxWidth: '1920px',
  containerRadius: '24px',
  fileContainerSize: '500px',
  formsDisplay: 'block',
  headerBackgroundColor: 'transparent',
  headerPadding: '20px 25px',
  margin: '24px',
  mobileHeaderPadding: '0px',
  sceneBackgroundColor: 'transparent',
  tabTitleBottomPadding: '0px',
  titleBottomPadding: '15px',
  titleColor: palette.grey300,
  titleTopPadding: '10px',
  uploadIconDisplay: 'flex',
};

const maintenanceInfo = {
  color: 'red',
  fontSize: '18px',
  margin: '20px',
};

const icon = {
  left: '0',
  position: 'static',
  top: '0',
};

const finishButton = {
  bottom: 'auto',
  height: 'initial',
  left: '0%',
  marginLeft: '0px',
  mobileBorderRadius: '9999px',
  mobileBottom: '0',
  mobileLeft: '0',
  mobileMargin: '0px',
  mobileWidth: '100%',
  position: 'initial',
  transformFrom: 'scale(0.5)',
  transformTo: 'scale(1)',
  width: 'initial',
};

const uploadButton = {
  backgroundColor: colors.info,
  buttonCase: 'uppercase',
  color: colors.white,
  height: 'initial',
  hover: colors.hover,
  iconColor: colors.white,
  iconSize: '0',
  marginLeft: 'initial',
  transform: '0px',
};

const toaster = {
  bottom: '0',
  radius: '10px',
  right: '0',
};

const customMediaQuery = (maxWidth: number) =>
  `@media (max-width: ${maxWidth}px)`;

const breakpoints = {
  desktop: 1200,
  mobile: 600,
  tablet: 700,
};

const theme = {
  breakpoints,
  buttonType,
  colors,
  finishButton,
  font(fontType: FontType, fontSize: FontSize) {
    const { font, weight } = this.fonts[fontType];

    return `
      color: ${this.fonts.fontColors[fontSize]};
      font-size: ${this.fonts.size[fontSize]};
      line-height: ${this.fonts.lineHeight[fontSize]};
      font-family: ${font};
      font-weight: ${weight};
      letter-spacing: ${this.fonts.letterSpacing[fontSize]};
    `;
  },
  fonts: {
    fontColors: {
      [FontSize.Inherit]: colors.text,
      [FontSize.Large]: colors.text,
      [FontSize.Medium]: colors.text,
      [FontSize.Small]: colors.text,
      [FontSize.Fine]: colors.text,
      [FontSize.H1]: colors.text,
      [FontSize.H2]: colors.textDarker,
      [FontSize.H3]: colors.textDarker,
      [FontSize.H4]: colors.text,
      [FontSize.H5]: colors.text,
      [FontSize.H6]: colors.text,
    },
    letterSpacing: {
      [FontSize.Inherit]: 'normal',
      [FontSize.Large]: '0.5px',
      [FontSize.Medium]: '.86px',
      [FontSize.Small]: 'normal',
      [FontSize.Fine]: 'normal',
      [FontSize.H1]: 'normal',
      [FontSize.H2]: 'normal',
      [FontSize.H3]: 'normal',
      [FontSize.H4]: 'normal',
      [FontSize.H5]: 'normal',
      [FontSize.H6]: 'normal',
    },
    lineHeight: {
      [FontSize.Inherit]: 'normal',
      [FontSize.Large]: `${70 / 60}`,
      [FontSize.Medium]: `${45 / 32}`,
      [FontSize.Small]: `${20 / 18}`,
      [FontSize.Fine]: 'normal',
      [FontSize.H1]: `${70 / 60}`,
      [FontSize.H2]: `${45 / 32}`,
      [FontSize.H3]: `${20 / 18}`,
      [FontSize.H4]: `${34 / 28}`,
      [FontSize.H5]: `${34 / 28}`,
      [FontSize.H6]: `${34 / 28}`,
    },
    [FontType.Light]: {
      font: 'Roboto',
      weight: '200',
    },
    [FontType.Regular]: {
      font: 'Roboto',
      weight: '400',
    },
    [FontType.SemiBold]: {
      font: 'Roboto',
      weight: '600',
    },
    [FontType.Strong]: {
      font: 'Roboto',
      weight: '700',
    },
    size: {
      [FontSize.Inherit]: '1em',
      [FontSize.Large]: '1.25em',
      [FontSize.Medium]: '1em',
      [FontSize.Small]: '.75em',
      [FontSize.Fine]: '.5em',
      [FontSize.H1]: '101px',
      [FontSize.H2]: '77px',
      [FontSize.H3]: '58px',
      [FontSize.H4]: '36px',
      [FontSize.H5]: '28px',
      [FontSize.H6]: '28px',
    },
  },
  icon,
  inputType,
  logo,
  maintenanceInfo,
  media: {
    custom: customMediaQuery,
    desktop: customMediaQuery(breakpoints.desktop),
    mobile: customMediaQuery(breakpoints.mobile),
    tablet: customMediaQuery(breakpoints.tablet),
  },
  palette,
  toaster,
  uploadButton,
  uploadPage,
};

export type ThemeType = typeof theme;

export default theme;
